import React, { useState, useEffect, useRef } from 'react';
import { Box, Button, TextField, Typography, InputAdornment, IconButton } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { getAuth, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import Navbar from './Navbar';
import Alerts from './Alerts';
import { useNavigate } from 'react-router-dom';
import { BrowserQRCodeReader } from '@zxing/browser';

const Login = ({ onLoginSuccess, toggleDarkMode, darkMode }) => {
  const [userId, setUserId] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info',
  });
  const [isCustomerMode, setIsCustomerMode] = useState(false);
  const [billingPreference, setBillingPreference] = useState(null);
  const navigate = useNavigate();
  const auth = getAuth();
  const videoRef = useRef(null);
  const codeReaderRef = useRef(null);
  const streamRef = useRef(null);
  const lastErrorTimeRef = useRef(0);

  const handleLogin = async (e) => {
    e.preventDefault();

    try {
      const pseudoEmail = `${userId}@platterly.in`;
      console.log('Login: Attempting login with userId:', userId, 'pseudoEmail:', pseudoEmail);
      const userCredential = await signInWithEmailAndPassword(auth, pseudoEmail, password);
      const user = userCredential.user;

      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
      if (!userDoc.exists()) {
        throw new Error('User data not found in Firestore');
      }

      const userData = userDoc.data();
      const role = userData.role;
      let companyCode = null;
      let companyName = null;
      let unitCode = null;
      let unitType = null;
      let isPureVeg = false;

      if (role !== 'softwareadmin') {
        const parts = userData.userId.split('#');
        if (parts.length !== 2 || !parts[0]) {
          throw new Error('Invalid user ID format. Expected format: companyCode#userId or unitCode#userId');
        }
        const prefix = parts[0];

        if (['unit_manager', 'kitchen_staff', 'servers'].includes(role)) {
          unitCode = prefix;
          const unitDocRef = doc(db, 'units', unitCode);
          const unitDoc = await getDoc(unitDocRef);
          if (!unitDoc.exists()) {
            throw new Error(`Unit with code ${unitCode} not found`);
          }
          const unitData = unitDoc.data();
          companyCode = unitData.companyCode;
          unitType = unitData.type || 'Unknown';
          companyName = unitData.companyName || 'Unknown Company';
          isPureVeg = unitData.isPureVeg || false;
        } else if (role === 'admin') {
          companyCode = prefix;
          const companyDocRef = doc(db, 'companies', companyCode);
          const companyDoc = await getDoc(companyDocRef);
          if (!companyDoc.exists()) {
            throw new Error(`Company with code ${companyCode} not found`);
          }
          companyName = companyDoc.data().name || 'Unknown Company';
          isPureVeg = companyDoc.data().isPureVeg || false;
        } else {
          throw new Error(`Unsupported role: ${role}`);
        }
      }

      sessionStorage.setItem('userId', userData.userId);
      if (companyCode) {
        sessionStorage.setItem('companyCode', companyCode);
      }
      if (unitCode) {
        sessionStorage.setItem('unitCode', unitCode);
      }
      if (unitType) {
        sessionStorage.setItem('unitType', unitType);
      }
      if (isPureVeg) {
        sessionStorage.setItem('isPureVeg', isPureVeg.toString());
      }

     
      setSnackbar({
        open: true,
        message: `Welcome ${userData.userId}!`,
        severity: 'success',
      });

      setTimeout(() => {

      localStorage.setItem('userData', JSON.stringify(userData));
      console.info('Login: User authenticated successfully', { userId: userData.userId, role, companyCode, unitCode, unitType, companyName, isPureVeg });
      

        console.log('Login: Calling onLoginSuccess with:', {
          role,
          userId: userData.userId,
          companyCode,
          companyName,
          unitCode,
          unitType,
          isPureVeg,
        });
        onLoginSuccess(role, userData.userId, { companyCode, companyName, unitCode, unitType, isPureVeg });

        let navigateTo;
        if (role === 'softwareadmin') {
          navigateTo = '/dashboard';
        } else if (role === 'admin') {
          navigateTo = `/admin/home?code=${companyCode}&name=${encodeURIComponent(companyName)}&isPureVeg=${isPureVeg}`;
        } else {
          navigateTo = `/${role}/home?code=${companyCode}&unit=${unitCode}&type=${unitType}&name=${encodeURIComponent(companyName)}&isPureVeg=${isPureVeg}`;
        }
        console.log('Login: Navigating to:', navigateTo);
        navigate(navigateTo, {
          state: { userId: userData.userId, companyCode, companyName, unitCode, unitType, isPureVeg },
        });
      }, 1000);
    } catch (error) {
      console.error('Login: Error:', { error, userId });
      setSnackbar({
        open: true,
        message: error.message || 'Login failed. Please check your credentials.',
        severity: 'error',
      });
    }
  };

  const handleCustomerRedirect = async () => {
    console.log('Login: Switching to customer QR code scanner mode');
    try {
      await signOut(auth);
      console.log('Login: Signed out any existing user');
    } catch (error) {
      console.warn('Login: No user was signed in or sign-out failed, proceeding to QR scanner:', error.message);
    }
    sessionStorage.clear();
    setIsCustomerMode(true);
  };

  const handleBackToLogin = () => {
    console.log('Login: Returning to login mode');
    setIsCustomerMode(false);
  };

  const handleManualEntry = () => {
    console.log('Login: Navigating to manual QR code entry');
    setIsCustomerMode(false);
    navigate('/customer/home', { state: { isCustomer: true } });
  };

  useEffect(() => {
    if (!isCustomerMode) return;

    codeReaderRef.current = new BrowserQRCodeReader();
    let isScanning = true;

    const startScanner = async () => {
      try {
        console.log('Login: Checking video element availability');
        if (!videoRef.current) {
          throw new Error('Video element not found');
        }
        console.log('Login: Video element ready, dimensions:', {
          width: videoRef.current.offsetWidth,
          height: videoRef.current.offsetHeight,
        });

        console.log('Login: Requesting camera permissions');
        let stream;
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: { ideal: 'environment' } },
          });
        } catch (err) {
          console.warn('Login: Rear camera failed, trying any camera:', err);
          stream = await navigator.mediaDevices.getUserMedia({ video: true });
        }
        console.log('Login: Camera permissions granted, stream active:', stream.active);
        streamRef.current = stream;

        // Assign stream to video element
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        console.log('Login: Video playback started, video dimensions:', {
          videoWidth: videoRef.current.videoWidth,
          videoHeight: videoRef.current.videoHeight,
        });

        // Get available video devices
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(device => device.kind === 'videoinput');
        if (videoDevices.length === 0) {
          throw new Error('No camera devices found');
        }

        // Prefer rear camera, fallback to any available camera
        const selectedCamera = videoDevices.find(device => device.label.toLowerCase().includes('back')) || videoDevices[0];
        console.log('Login: Selected camera:', selectedCamera.label, selectedCamera.deviceId);

        // Wait briefly to ensure video is ready
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Timeout for camera initialization
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Camera initialization timed out after 10 seconds')), 10000);
        });

        const fetchBillingPreference = async (companyCode, unitCode) => {
  try {
    const q = query(
      collection(db, "preferences"),
      where("companyCode", "==", companyCode),
      where("unitCode", "==", unitCode),
      where("preferenceName", "==", "Billing Process")
    );

    const snapshot = await getDocs(q);

    if (!snapshot.empty) {
      const value = Number(snapshot.docs[0].data().preferenceValue);
      setBillingPreference(value);
      return value;
    }
  } catch (err) {
    console.error("Billing fetch error:", err);
  }

  return null;
};
        // Start decoding
        await Promise.race([
          codeReaderRef.current.decodeFromVideoDevice(
            selectedCamera.deviceId,
            videoRef.current,
            async (result, error) => {
              if (!isScanning) return;
              if (result) {
                console.log('Login: QR code scanned:', result.getText());
                try {
                  const url = new URL(result.getText());
                  const companyCode = url.searchParams.get('code');
                  const unitCode = url.searchParams.get('unit');
                  const tableId = url.searchParams.get('id');
                  const serviceArea = url.searchParams.get('serviceArea'); // ✅ NEW
                  if (!companyCode || !unitCode || !tableId) {
                    throw new Error('Invalid QR code format. Missing code, unit, or id.');
                  }

                  console.log('Login: Navigating to customer home with:', { companyCode, unitCode, tableId, serviceArea });
                  isScanning = false;
                  const billing = await fetchBillingPreference(companyCode, unitCode);

if (billing === 0 || billing === 1) {
  console.log("Billing flow bypass → going directly to home");

  // ✅ FIX: store values for home page
  sessionStorage.setItem("companyCode", companyCode);
  sessionStorage.setItem("unitCode", unitCode);
  sessionStorage.setItem("tableId", tableId);
  sessionStorage.setItem("serviceArea", serviceArea || "");

  navigate(`/customer/home?code=${companyCode}&unit=${unitCode}&id=${tableId}&serviceArea=${serviceArea || ''}`, {
    state: {
      companyCode,
      unitCode,
      tableId,
      serviceArea,
      isCustomer: true,
    },
  });

  return;
}
                  /*navigate(`/customer/home?code=${companyCode}&unit=${unitCode}&id=${tableId}`, {
                    state: { companyCode, unitCode, tableId, isCustomer: true },
                  });*/
                  /*navigate(
        `/customer/details?code=${companyCode}&unit=${unitCode}&id=${tableId}&serviceArea=${serviceArea || ''}`, // ✅ add here
        {
          state: {
            companyCode,
            unitCode,
            tableId,
            serviceArea, // ✅ also pass in state
            isCustomer: true,
          },
        }
      );*/
      const cartsRef = collection(db, "carts");

const q = query(
  cartsRef,
  where("companyCode", "==", companyCode),
  where("unitCode", "==", unitCode),
  where("tableId", "==", tableId),
  where("serviceArea", "==", serviceArea)
);

const snapshot = await getDocs(q);

let existingCart = null;

/*snapshot.forEach(doc => {
  const data = doc.data();

  console.log("👉 Checking cart:", data.cartId, data.cartStatus, data.items?.length);

  if (
    data.items?.length > 0 &&   // ✅ has items
    data.cartStatus !== "Empty" // ✅ not empty cart
  ) {
    if (
      !existingCart ||
      data.updatedAt?.seconds > existingCart.updatedAt?.seconds
    ) {
      existingCart = data;
    }
  }
});*/
snapshot.forEach(doc => {
  const data = doc.data();

  if (
    !existingCart ||
    data.updatedAt?.seconds > existingCart?.updatedAt?.seconds
  ) {
    existingCart = data;
  }
});

console.log("Login Scan - existingCart:", existingCart);

if (existingCart) {
  // 🔥 OTP flow
 navigate(
  `/customer/join-cart?code=${companyCode}&unit=${unitCode}&id=${tableId}&serviceArea=${serviceArea || ''}`,
  {
    state: {
      existingCart,
      companyCode,
      unitCode,
      tableId,
      serviceArea
    }
  }
);
} else {
  // ✅ normal flow
  navigate(
    `/customer/details?code=${companyCode}&unit=${unitCode}&id=${tableId}&serviceArea=${serviceArea || ''}&isCustomer=true`,
    {
      state: {
        companyCode,
        unitCode,
        tableId,
        serviceArea,
        isCustomer: true,
      },
    }
  );
}
                } catch (err) {
                  console.error('Login: Error processing QR code:', err);
                  setSnackbar({
                    open: true,
                    message: 'Invalid QR code. Please scan a valid Platterly QR code.',
                    severity: 'error',
                  });
                }
              }
              if (error && error.name !== 'NotFoundException') {
                console.error('Login: QR scan error:', error.name || 'Unknown', error.message || 'No message');
                setSnackbar({
                  open: true,
                  message: 'Failed to scan QR code. Please try again or use manual entry.',
                  severity: 'error',
                });
              } else if (error && error.name === 'NotFoundException') {
                const now = Date.now();
                if (now - lastErrorTimeRef.current > 5000) {
                  console.log('Login: No QR code detected, continuing to scan');
                  lastErrorTimeRef.current = now;
                }
              }
            }
          ),
          timeoutPromise,
        ]);
        console.log('Login: QR code scanner started');
      } catch (error) {
        console.error('Login: Error starting scanner:', error.name, error.message);
        setSnackbar({
          open: true,
          message: `Error accessing camera: ${error.message}. Please ensure camera permissions are granted or use manual entry.`,
          severity: 'error',
        });
      }
    };

    startScanner();

    return () => {
      isScanning = false;
      if (codeReaderRef.current) {
        console.log('Login: Stopping QR code scanner');
        try {
          if (typeof codeReaderRef.current.stop === 'function') {
            codeReaderRef.current.stop();
          } else {
            console.warn('Login: stop method not available on codeReader');
          }
        } catch (err) {
          console.error('Login: Error stopping scanner:', err);
        }
      }
      if (streamRef.current) {
        console.log('Login: Stopping camera stream');
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    };
  }, [isCustomerMode, navigate]);

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundImage: darkMode
          ? 'url(https://img.freepik.com/free-photo/flat-lay-mexican-food-composition-with-copyspace_23-2148140229.jpg?w=360)'
          : 'url(https://img.freepik.com/free-photo/spaghetii-bolognese-ingredients-copy-space_23-2148551757.jpg?semt=ais_hybrid&w=740)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <Navbar toggleDarkMode={toggleDarkMode} darkMode={darkMode} />
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          px: 2,
          py: 4,
        }}
      >
        <Box
          sx={{
            width: 400,
            p: 4,
            borderRadius: 3,
            boxShadow: darkMode ? '0 8px 24px rgba(0,0,0,0.5)' : '0 8px 24px rgba(0,0,0,0.1)',
            backgroundColor: darkMode ? '#2A2A40' : 'rgba(255, 255, 255, 0.9)',
            border: darkMode ? '1px solid #4b4b6e' : '1px solid #e0e0e0',
          }}
        >
          {isCustomerMode ? (
            <>
              <Typography
                variant="h5"
                align="center"
                gutterBottom
                sx={{ mb: 3, color: darkMode ? '#d1d5db' : '#1f2937' }}
              >
                Scan QR Code
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  style={{ width: '100%', maxWidth: 400, borderRadius: 8, backgroundColor: '#000' }}
                />
                <Typography
                  variant="body2"
                  sx={{ color: darkMode ? '#9ca3af' : '#666', textAlign: 'center' }}
                >
                  Scan the QR code on your table to access the menu.
                </Typography>
                {/* <Button
                  variant="contained"
                  sx={{
                    bgcolor: darkMode ? '#4b5563' : '#735DA5',
                    '&:hover': { bgcolor: darkMode ? '#6b7280' : '#a583f2' },
                  }}
                  onClick={handleManualEntry}
                >
                  Enter QR Code Manually
                </Button> */}
                <Button
                  variant="outlined"
                  sx={{
                    mt: 1,
                    color: darkMode ? '#d1d5db' : '#1f2937',
                    borderColor: darkMode ? '#4b4b6e' : '1px solid #e0e0e0',
                    '&:hover': { borderColor: darkMode ? '#6b7280' : '#735DA5' },
                  }}
                  onClick={handleBackToLogin}
                >
                  Back to Login
                </Button>
              </Box>
            </>
          ) : (
            <>
              <Typography
                variant="h5"
                align="center"
                gutterBottom
                sx={{ marginBottom: 2, fontWeight: 'bold', color: darkMode ? '#d1d5db' : '#2c3e50' }}
              >
                Employee Login
              </Typography>
              <Box
                component="form"
                onSubmit={handleLogin}
                sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
              >
                <TextField
                  label="User ID"
                  variant="outlined"
                  fullWidth
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  sx={{
                    '& .MuiInputBase-input': { color: darkMode ? '#d1d5db' : '#1f2937' },
                    '& .MuiInputLabel-root': { color: darkMode ? '#9ca3af' : '#64748b' },
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: darkMode ? '#4b4b6e' : '#e0e0e0' },
                      '&:hover fieldset': { borderColor: darkMode ? '#6b7280' : '#735DA5' },
                    },
                  }}
                />
                <TextField
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  variant="outlined"
                  fullWidth
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleTogglePasswordVisibility}
                          edge="end"
                          sx={{ color: darkMode ? '#9ca3af' : '#64748b' }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiInputBase-input': { color: darkMode ? '#d1d5db' : '#1f2937' },
                    '& .MuiInputLabel-root': { color: darkMode ? '#9ca3af' : '#64748b' },
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: darkMode ? '#4b4b6e' : '#e0e0e0' },
                      '&:hover fieldset': { borderColor: darkMode ? '#6b7280' : '#735DA5' },
                    },
                  }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  sx={{
                    mt: 2,
                    bgcolor: darkMode ? '#4b5563' : '#735DA5',
                    '&:hover': { bgcolor: darkMode ? '#6b7280' : '#a583f2' },
                  }}
                  fullWidth
                >
                  Login
                </Button>
                <Typography
                  variant="body2"
                  align="center"
                  sx={{ color: darkMode ? '#9ca3af' : '#666' }}
                >
                  OR
                </Typography>
                <Typography
                  variant="body2"
                  align="center"
                  sx={{
                    color: darkMode ? '#a583f2' : '#735DA5',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    '&:hover': {
                      color: darkMode ? '#d8b4fe' : '#a583f2',
                    },
                  }}
                  onClick={handleCustomerRedirect}
                >
                  Continue as Customer
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </Box>
      <Alerts snackbar={snackbar} handleClose={handleSnackbarClose} darkMode={darkMode} />
    </Box>
  );
};

export default Login;